
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { Customer } from '@/lib/supabase/customers/types';

interface UseCustomerOperationsProps {
  organizationId: string | null;
  fetchCustomers: () => Promise<void>;
  setEditingCustomerId: (id: string | null) => void;
  customers: Customer[];
  shopId?: string;
}

export const useCustomerOperations = ({
  organizationId,
  fetchCustomers,
  setEditingCustomerId,
  customers,
  shopId
}: UseCustomerOperationsProps) => {
  const { toast } = useToast();
  const [deleting, setDeleting] = useState(false);

  // Handle customer edit
  const handleEditCustomer = (id: string) => {
    setEditingCustomerId(id);
  };

  // Handle customer add
  const handleAddCustomer = async () => {
    if (!organizationId) {
      toast({
        title: 'Erreur',
        description: 'ID de l\'organisation non disponible',
        variant: 'destructive',
      });
      return;
    }

    // Verify that a shop is selected
    if (!shopId) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner une boutique avant d\'ajouter un client',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Directly open the customer edit form with a new ID
      console.log("Opening new customer form with shopId:", shopId);
      setEditingCustomerId('new');
    } catch (error) {
      console.error('Error creating customer:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de créer le client',
        variant: 'destructive',
      });
    }
  };

  // Delete a customer
  const handleDeleteCustomer = async (id: string): Promise<boolean> => {
    try {
      setDeleting(true);
      
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      toast({
        title: 'Client supprimé',
        description: 'Le client a été supprimé avec succès',
      });
      
      // Refresh the list
      fetchCustomers();
      return true;
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le client',
        variant: 'destructive',
      });
      return false;
    } finally {
      setDeleting(false);
    }
  };

  // Bulk delete customers
  const handleBulkDeleteCustomers = async (ids: string[]) => {
    if (ids.length === 0) return;
    
    try {
      setDeleting(true);
      
      // Process deletions sequentially to avoid rate limiting
      let deletedCount = 0;
      
      for (const id of ids) {
        const { error } = await supabase
          .from('customers')
          .delete()
          .eq('id', id);
          
        if (!error) deletedCount++;
      }
      
      if (deletedCount > 0) {
        toast({
          title: `${deletedCount} client(s) supprimé(s)`,
          description: 'Les clients ont été supprimés avec succès',
        });
        
        // Refresh the list
        fetchCustomers();
      }
    } catch (error) {
      console.error('Error bulk deleting customers:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer certains clients',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  return {
    deleting,
    handleAddCustomer,
    handleEditCustomer,
    handleDeleteCustomer,
    handleBulkDeleteCustomers,
    shopId
  };
};

// For backward compatibility
export const useClientOperations = useCustomerOperations;
